import { environment } from '@environment';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';

export interface Section {
  accentColor: string;
  bgColor: string;
  title: string;
  shortTitle: string;
  icon: string;
  cardsList: HomeBCardContent[];
  id: SectionIdEnum;
  new?: boolean;
}

export interface HomeBCardContent {
  title: string;
  img: string;
  description?: string;
  link?: string[];
  url?: string;
  trackEventAction?: string;
  trackEventLabel?: string;
  gtag?: string;
  ariaLabel: string;
  new?: boolean;
}

export enum SectionIdEnum {
  jobs = 'emploi-stages',
  trainings = 'orientation-formation',
  helps = 'aides-accompagnement',
  engagment = 'engagement-benevolat',
}

export const SECTIONS: Section[] = [
  {
    id: SectionIdEnum.helps,
    accentColor: '#526AB6',
    bgColor: '#F5F8FD',
    title: 'Aides et accompagnement',
    shortTitle: 'Aides',
    icon: 'life-ring',
    new: true,
    cardsList: [
      {
        title: "Je découvre le Contrat d'Engagement Jeune !",
        description: "Un parcours personnalisé pour m'aider à définir mon projet professionnel et trouver un emploi",
        img: `${environment.wizbiiImages}/home/thumbnails/cej_thumbnail.jpg`,
        link: ['/', FeaturesRoutingEnum.CEJ],
        trackEventAction: 'CEJ',
        ariaLabel: 'Naviguer vers la page du Contrat Engagement Jeune',
        new: true,
      },
      {
        title: 'J’accède à mes aides financières',
        description:
          'Avec La Boussole, trouvez les aides auxquelles vous avez droit : logement, santé, mobilité, emploi, culture, etc.',
        img: `${environment.wizbiiImages}/home/young-thumbnails/aides.jpg`,
        link: ['/', FeaturesRoutingEnum.AccessHelps],
        trackEventAction: 'Aides',
        ariaLabel: 'Naviguer vers la page des aides financières',
      },
      {
        title: 'J’ai besoin d’un accompagnement',
        description:
          'Retrouvez les structures proches de chez vous pouvant vous aider dans vos démarches ou votre parcours.',
        img: `${environment.wizbiiImages}/home/young-thumbnails/support.jpg`,
        link: ['/', FeaturesRoutingEnum.YouthInfoCenter],
        trackEventAction: 'Accompagnement',
        ariaLabel: 'Naviguer vers la page accompagnement',
      },
      {
        title: 'Je souhaite échanger avec un mentor',
        description:
          'Une association vous recontacte pour vous proposer le programme de mentorat adapté à vos besoins.',
        img: `${environment.wizbiiImages}/home/young-thumbnails/mentor.jpg`,
        link: ['/', FeaturesRoutingEnum.Mentoring],
        trackEventAction: 'Mentorat',
        ariaLabel: 'Naviguer vers la page mentorat',
      },
      {
        title: 'J’ai entre 16 et 18 ans',
        description:
          "Je suis sans formation, sans emploi et j'ai entre 16 et 18 ans. Je me fais accompagner pour construire mon avenir.",
        img: `${environment.wizbiiImages}/home/young-thumbnails/obligation_formation.png`,
        link: ['/', FeaturesRoutingEnum.TrainingObligation],
        trackEventAction: '16-18 ans',
        ariaLabel: 'Naviguer vers la page obligation de formation',
      },
      {
        title: 'Je consulte les mesures jeunes',
        description: "Découvrez les solutions pour aider chacun d'entre vous à accéder à l'emploi",
        img: `${environment.wizbiiImages}/home/young-thumbnails/mesures_jeunes.png`,
        link: ['/', FeaturesRoutingEnum.Young],
        trackEventAction: 'Mesures Jeunes',
        ariaLabel: 'Naviguer vers la page des mesures jeunes',
      },
    ],
  },
  {
    id: SectionIdEnum.jobs,
    accentColor: '#0E7B47',
    bgColor: '#E7F2ED',
    title: 'Emploi et stage',
    shortTitle: 'Emploi',
    icon: 'luggage-outline',
    cardsList: [
      {
        title: 'Je postule à un job',
        description: 'Plus de 300 000 offres d’emploi sélectionnées spécialement pour vous.',
        img: `${environment.wizbiiImages}/home/young-thumbnails/job.jpg`,
        link: ['/', FeaturesRoutingEnum.Jobs],
        trackEventAction: 'Emploi',
        gtag: 'DC-3048978/emplo253/vigemplo+unique',
        ariaLabel: 'Naviguer vers la liste des emplois',
      },
      {
        title: 'Je postule à un stage',
        description: 'Plus de 20 000 offres de stage sélectionnées spécialement pour vous.',
        img: `${environment.wizbiiImages}/home/young-thumbnails/internship.jpg`,
        link: ['/', FeaturesRoutingEnum.Internships],
        trackEventAction: 'Stages',
        ariaLabel: 'Naviguer vers la liste des stages',
      },
      {
        title: 'Je postule à un job étudiant',
        description: 'Plus de 10 000 offres d’emploi compatibles avec vos études (moins de 15h par semaine)',
        img: `${environment.wizbiiImages}/home/young-thumbnails/summer_job.png`,
        link: ['/', FeaturesRoutingEnum.StudentJobs],
        trackEventAction: 'Jobs étudiant',
        ariaLabel: 'Naviguer vers la liste des emplois étudiants',
      },
      {
        title: 'Je cherche une alternance',
        description: "Trouvez votre formation et votre entreprise pour concrétiser vos projets d'alternance.",
        img: `${environment.wizbiiImages}/home/young-thumbnails/apprentice.png`,
        link: ['/', FeaturesRoutingEnum.Apprentice],
        trackEventAction: 'Apprentissage',
        ariaLabel: "Naviguer vers la liste des offres d'apprentissage",
      },
      {
        title: 'Je cherche une expérience en Europe',
        description:
          "Retrouvez des offres d'emploi, des stages, des VIE | VIA et des aides financières pour une expérience en Europe.",
        img: `${environment.wizbiiImages}/europe/hero.jpg`,
        link: ['/', FeaturesRoutingEnum.pfue],
        trackEventAction: 'Europe',
        ariaLabel: 'Naviguer vers la liste des emplois en Europe',
      },
      {
        title: 'J’enregistre ma vidéo de motivation',
        description:
          'Faites découvrir votre personnalité et votre motivation à des recruteurs à travers une vidéo de motivation.',
        img: `${environment.wizbiiImages}/home/young-thumbnails/visiotalent.png`,
        link: ['/', FeaturesRoutingEnum.Visiotalent],
        trackEventAction: 'Visiotalent',
        ariaLabel: 'Naviguer vers la page vidéo de motivation',
      },
      {
        title: 'Je souhaite entreprendre',
        description: 'Retrouvez les conseils, outils et structures d’accompagnement pour vous aider à entreprendre.',
        img: `${environment.wizbiiImages}/home/young-thumbnails/entreprendre.jpg`,
        link: ['/', FeaturesRoutingEnum.CreateCompany],
        trackEventAction: 'Entreprendre',
        ariaLabel: 'Naviguer vers la page entreprendre',
      },
      {
        title: 'Je souhaite entreprendre à Marseille',
        description: "Profitez d'un accompagnement spécialisé et d'une dotation de 3 000€ pour lancer votre entreprise",
        img: `${environment.wizbiiImages}/marseille/hero.jpg`,
        link: ['/', FeaturesRoutingEnum.Marseille],
        trackEventAction: 'Entreprendre Marseille',
        ariaLabel: 'Naviguer vers la page entreprendre à Marseille ',
      },
    ],
  },
  {
    id: SectionIdEnum.trainings,
    accentColor: '#975394',
    bgColor: '#F6EEF5',
    title: 'Orientation et formation',
    shortTitle: 'Orientation',
    icon: 'compass',
    cardsList: [
      {
        title: 'Je trouve une formation',
        description: 'Plus de 60 000 formations accessibles pour réaliser votre projet professionnel',
        img: `${environment.wizbiiImages}/home/young-thumbnails/formation.jpg`,
        link: ['/', FeaturesRoutingEnum.Trainings],
        trackEventAction: 'Formation',
        ariaLabel: 'Naviguer vers la liste des formations',
      },
      {
        title: 'Je participe à un Job Dating ',
        description: 'Plus de 500 entreprises viennent à votre rencontre, dans 60 départements à travers la France.',
        img: `${environment.wizbiiImages}/home/young-thumbnails/event.jpg`,
        link: ['/', FeaturesRoutingEnum.JobDating],
        trackEventAction: 'Job Dating',
        ariaLabel: 'Naviguer vers la liste des job dating',
      },
      {
        title: 'Je participe à un événement',
        description: 'Des centaines d’événements de recrutement pour tous les jeunes, partout en France.',
        img: `${environment.wizbiiImages}/home/young-thumbnails/all_events.png`,
        link: ['/', FeaturesRoutingEnum.Events],
        trackEventAction: 'Evenements',
        ariaLabel: 'Naviguer vers la liste des événements',
      },
      {
        title: 'Je crée mon CV',
        description: "Mettez en avant vos compétences dans un CV, même si vous pensez ne pas avoir d'expérience",
        img: `${environment.wizbiiImages}/home/young-thumbnails/cv.png`,
        link: ['/', FeaturesRoutingEnum.DiagorienteCv],
        trackEventAction: 'Créer CV',
        ariaLabel: 'Naviguer vers la page pour créer un cv',
      },
      {
        title: "Je définis mes centres d'intérêt",
        description: "Précisez vos envies à l'aide d'un outil d'aide à l'orientation",
        img: `${environment.wizbiiImages}/home/young-thumbnails/interests.png`,
        link: ['/', FeaturesRoutingEnum.DiagorienteInterests],
        trackEventAction: 'Trouver Mon Métier',
        ariaLabel: "Naviguer vers la page d'aide à l'orientation",
      },
    ],
  },

  {
    id: SectionIdEnum.engagment,
    accentColor: '#761B3F',
    bgColor: '#F1E8EC',
    title: 'Engagement et bénévolat',
    shortTitle: 'Engagement',
    icon: 'hand-shake',
    cardsList: [
      {
        title: 'Je réalise une mission de service civique',
        description: 'Je réalise une mission citoyenne de 6 à 12 mois, donnant le droit à une indemnisation',
        img: `${environment.wizbiiImages}/home/young-thumbnails/service_civique.png`,
        link: ['/', FeaturesRoutingEnum.CivicService],
        trackEventAction: 'Service Civique',
        ariaLabel: 'Naviguer vers la liste des missions du Service Civique',
      },
      {
        title: 'Je réalise une mission de bénévolat',
        description:
          "Je réalise une mission d'engagement civique courte auprès d'organisations publiques ou associatives",
        img: `${environment.wizbiiImages}/home/young-thumbnails/benevolat.png`,
        link: ['/', FeaturesRoutingEnum.Volunteer],
        trackEventAction: 'Bénévolat',
        ariaLabel: 'Naviguer vers la liste des missions de bénévolat',
      },
      {
        title: "Je découvre tous les dispositifs d'engagement",
        description: "Je découvre les différents dispositifs pour agir concrètement pour l'intérêt général",
        img: `${environment.wizbiiImages}/home/young-thumbnails/engagement.png`,
        link: ['/', FeaturesRoutingEnum.Commitment],
        trackEventAction: 'Engagement',
        ariaLabel: 'Naviguer vers la page engagement',
      },
    ],
  },
];
